import type { Directive, DirectiveBinding } from 'vue'
import { useOUStore } from '../stores/ou-store'

interface PermissionBinding {
  module: string
  permission: string | string[]
  mode?: 'any' | 'all' // For array permissions
}

/**
 * Directive to show/hide elements based on permissions
 *
 * Usage:
 * v-permission="{ module: 'users', permission: 'create' }"
 * v-permission="{ module: 'users', permission: ['create', 'update'], mode: 'any' }"
 */
export const vPermission: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<PermissionBinding>) {
    checkPermission(el, binding)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<PermissionBinding>) {
    checkPermission(el, binding)
  },
}

function checkPermission(el: HTMLElement, binding: DirectiveBinding<PermissionBinding>) {
  const ouStore = useOUStore()
  const { module, permission, mode = 'any' } = binding.value

  let hasAccess = false

  if (Array.isArray(permission)) {
    if (mode === 'all') {
      hasAccess = permission.every((p) => ouStore.hasPermission(module, p))
    } else {
      hasAccess = permission.some((p) => ouStore.hasPermission(module, p))
    }
  } else {
    hasAccess = ouStore.hasPermission(module, permission)
  }

  if (!hasAccess) {
    el.style.display = 'none'
  } else {
    el.style.display = ''
  }
}

/**
 * Directive to disable elements based on permissions
 *
 * Usage:
 * v-permission-disable="{ module: 'users', permission: 'update' }"
 */
export const vPermissionDisable: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<PermissionBinding>) {
    checkPermissionDisable(el, binding)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<PermissionBinding>) {
    checkPermissionDisable(el, binding)
  },
}

function checkPermissionDisable(el: HTMLElement, binding: DirectiveBinding<PermissionBinding>) {
  const ouStore = useOUStore()
  const { module, permission, mode = 'any' } = binding.value

  let hasAccess = false

  if (Array.isArray(permission)) {
    if (mode === 'all') {
      hasAccess = permission.every((p) => ouStore.hasPermission(module, p))
    } else {
      hasAccess = permission.some((p) => ouStore.hasPermission(module, p))
    }
  } else {
    hasAccess = ouStore.hasPermission(module, permission)
  }

  if (!hasAccess) {
    el.setAttribute('disabled', 'true')
    el.classList.add('permission-disabled')
  } else {
    el.removeAttribute('disabled')
    el.classList.remove('permission-disabled')
  }
}

export default {
  install(app: any) {
    app.directive('permission', vPermission)
    app.directive('permission-disable', vPermissionDisable)
  },
}
